import React from 'react'
import { motion } from 'framer-motion'
import { useMenuQuery } from '../queries/static'
import { Link } from 'gatsby'
import Logo from './logo'

interface Props {
  setIsOpen: (boolean) => void
  activeMenu: string
  setActiveMenu: (string) => void
}

const Sidebar: React.FC<Props> = ({ setIsOpen, activeMenu, setActiveMenu }) => {
  const data = useMenuQuery()

  const secondaryMenu = data?.secondaryMenu?.menuItems?.nodes

  const handleClose = (): void => {
    setIsOpen(false)
    setActiveMenu('')
  }

  const toggleActiveMenu = (menuTarget: string): void => {
    if (activeMenu === menuTarget) {
      setActiveMenu('')
    } else {
      setActiveMenu(menuTarget)
    }
  }

  const isActiveIcon = (target: string) => {
    if (activeMenu === target) {
      return (
        <svg xmlns='http://www.w3.org/2000/svg' width='12' height='2.571' viewBox='0 0 12 2.571'>
          <path
            d='M11.143,14.625H.857A.857.857,0,0,0,0,15.482v.857a.857.857,0,0,0,.857.857H11.143A.857.857,0,0,0,12,16.339v-.857A.857.857,0,0,0,11.143,14.625Z'
            transform='translate(0 -14.625)'
            fill='currentColor'
          />
        </svg>
      )
    }

    return (
      <svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'>
        <path
          d='M11.143,6.964H7.286V3.107a.857.857,0,0,0-.857-.857H5.571a.857.857,0,0,0-.857.857V6.964H.857A.857.857,0,0,0,0,7.821v.857a.857.857,0,0,0,.857.857H4.714v3.857a.857.857,0,0,0,.857.857h.857a.857.857,0,0,0,.857-.857V9.536h3.857A.857.857,0,0,0,12,8.679V7.821A.857.857,0,0,0,11.143,6.964Z'
          transform='translate(0 -2.25)'
          fill='currentColor'
        />
      </svg>
    )
  }

  return (
    <React.Fragment>
      <motion.div className={`w-full h-full flex z-30`}>
        <div className='flex flex-col bg-white w-full'>
          <div className='px-12 h-16 flex items-center justify-between border-b border-gray-light'>
            <Link to='/'>
              <Logo size='small' />
            </Link>
            <button
              onClick={() => {
                handleClose()
              }}
              className='sidebar__close p-2 text-gray-silver hover:text-blue'
            >
              <svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'>
                <path
                  d='M8.275,11.625l3.411-3.411a1.072,1.072,0,0,0,0-1.516l-.758-.758a1.072,1.072,0,0,0-1.516,0L6,9.35,2.589,5.939a1.072,1.072,0,0,0-1.516,0L.314,6.7a1.072,1.072,0,0,0,0,1.516l3.411,3.411L.314,15.036a1.072,1.072,0,0,0,0,1.516l.758.758a1.072,1.072,0,0,0,1.516,0L6,13.9l3.411,3.411a1.072,1.072,0,0,0,1.516,0l.758-.758a1.072,1.072,0,0,0,0-1.516Z'
                  transform='translate(0 -5.625)'
                  fill='currentColor'
                />
              </svg>
            </button>
          </div>
          <div className='ctas py-6 px-12 flex items-center justify-center border-b border-gray-light'>
            {secondaryMenu.map((menuItem, i) => {
              {
                if (i === 0) {
                  return (
                    <a className={`btn btn-blue mr-6`} key={menuItem.path} href={menuItem.path}>
                      {menuItem.label}
                    </a>
                  )
                }
              }
              return (
                <Link className={`btn btn-aqua`} key={menuItem.path} to={menuItem.path}>
                  {menuItem.label}
                </Link>
              )
            })}
          </div>
          <motion.div layout className='menu'>
            <motion.div layout className='menu-item menu-item__growth'>
              <motion.button
                layout
                onClick={() => {
                  toggleActiveMenu('/physician-strategy-suite/')
                }}
                className={`h-16 focus:outline-none py-4 px-12 border-b border-gray-light flex items-center justify-between text-gray hover:text-blue w-full ${
                  activeMenu === '/physician-strategy-suite/' ? '!text-blue' : ''
                }`}
              >
                <span className='body-2'>{data?.growth?.label}</span>
                <div className='p-2'>{isActiveIcon('/physician-strategy-suite/')}</div>
              </motion.button>
              {activeMenu === '/physician-strategy-suite/' && (
                <motion.div
                  layout
                  initial={{ x: -25, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ ease: 'easeOut', delay: 0.2 }}
                  className='submenu py-4 px-12 border-b border-gray-light flex flex-col'
                >
                  {data?.growth?.childItems?.nodes.map((childItem) => {
                    return (
                      <Link
                        className='body-1 mb-2 text-gray hover:text-blue'
                        key={childItem?.path}
                        to={childItem?.path}
                        activeClassName='font-bold !text-blue pointer-events-none'
                        partiallyActive={true}
                      >
                        {childItem?.label}
                      </Link>
                    )
                  })}
                </motion.div>
              )}
            </motion.div>
            <motion.div layout className='menu-item menu-item__education'>
              <motion.button
                layout
                onClick={() => {
                  toggleActiveMenu('/education-center/')
                }}
                className={`h-16 focus:outline-none py-4 px-12 border-b border-gray-light flex items-center justify-between text-gray hover:text-blue w-full ${
                  activeMenu === '/education-center/' ? '!text-blue' : ''
                }`}
              >
                <span className='body-2'>{data?.education?.label}</span>
                <div className='p-2'>{isActiveIcon('/education-center/')}</div>
              </motion.button>
              {activeMenu === '/education-center/' && (
                <motion.div
                  layout
                  initial={{ x: -25, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ ease: 'easeOut', delay: 0.2 }}
                  className='submenu py-4 px-12 border-b border-gray-light flex flex-col'
                >
                  {data?.education?.childItems?.nodes.map((childItem) => {
                    return (
                      <Link
                        className='body-1 mb-2 text-gray hover:text-blue'
                        key={childItem?.path}
                        to={childItem?.path}
                        activeClassName='font-bold !text-blue pointer-events-none'
                        partiallyActive={true}
                      >
                        {childItem?.label}
                      </Link>
                    )
                  })}
                </motion.div>
              )}
            </motion.div>
            <motion.div layout className='menu-item menu-item__success'>
              <Link
                className='py-4 px-12 border-b border-gray-light flex items-center justify-between text-gray hover:text-blue'
                to='/client-success/'
              >
                <span className='body-2'>Client Success</span>
              </Link>
            </motion.div>
            <motion.div layout className='menu-item menu-item__about'>
              <motion.button
                layout
                onClick={() => {
                  toggleActiveMenu('/about/')
                }}
                className={`h-16 focus:outline-none py-4 px-12 border-b border-gray-light flex items-center justify-between text-gray hover:text-blue w-full ${
                  activeMenu === '/about/' ? '!text-blue' : ''
                }`}
              >
                <span className='body-2'>{data?.about?.label}</span>
                <div className='p-2'>{isActiveIcon('/about/')}</div>
              </motion.button>
              {activeMenu === '/about/' && (
                <motion.div
                  layout
                  initial={{ x: -25, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ ease: 'easeOut', delay: 0.2 }}
                  className='submenu py-4 px-12 border-b border-gray-light flex flex-col'
                >
                  {data?.about?.childItems?.nodes.map((childItem) => {
                    {
                      if (childItem.acfSocialicons.hasSocialIcons) return
                    }
                    return (
                      <Link
                        className='body-1 mb-2 text-gray hover:text-blue'
                        key={childItem?.path}
                        to={childItem?.path}
                        activeClassName='font-bold !text-blue pointer-events-none'
                        partiallyActive={true}
                      >
                        {childItem?.label}
                      </Link>
                    )
                  })}
                </motion.div>
              )}
            </motion.div>
            <motion.div layout className='menu-item menu-item__social'>
              {data?.about?.childItems?.nodes.map((childItem) => {
                {
                  if (childItem.acfSocialicons.hasSocialIcons) {
                    return (
                      <div
                        key={childItem.id}
                        className='flex ml-4 py-4 px-12 items-center justify-center'
                      >
                        {childItem?.acfSocialicons?.socialIcons.map((social) => {
                          return (
                            <a
                              key={social?.socialIconLink}
                              className='mr-6'
                              href={social?.socialIconLink}
                              target='_blank'
                              rel='noreferrer noopener'
                            >
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: social?.socialIconCode,
                                }}
                              ></span>
                            </a>
                          )
                        })}
                      </div>
                    )
                  }
                }
              })}
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ ease: 'easeOut', duration: 0.25 }}
        className='fixed bg-black bg-opacity-20 w-full h-full z-20 top-0 left-0 lg:hidden'
      ></motion.div>
    </React.Fragment>
  )
}

export default Sidebar
